import {
    DashboardUrl,
    getClientsRequest,
    getCustomerRequest,
    LogoutPageUrl,
    AppPageUrl,
    rootPageUrl,
    MobilePageUrl,
    postSupportMessageRequest,
    getBackendVersionRequest,
    ReportUrl,
    UsersPageUrl,
    CampaignUrl,
    EditCampaignUrl,
    CreateClientUrl
} from "@common";
import { FetchingState } from "rd-redux-http";
import { LocationChangeAction, LOCATION_CHANGE, replace } from "react-router-redux";
import { all, put, select, takeEvery, take, call } from "redux-saga/effects";
import { AppState } from "../store";
import { storeTokens } from "../../api/utils";
import { sendSupportMessageAction, openNotificationAction, changeDashboardLinkAction } from "./actions";
import { isReportingOnlyCustomer } from "./selectors";
import { Action } from "redux";
import { ClientDataInterface } from "store/api";

function* loadCustomerAndClients(a: LocationChangeAction) {
    try {
        const state: AppState = yield select();

        if (!FetchingState.isSuccess(state.pagesState.customer)) {
            yield* getCustomerRequest.saga({});
        }
        if (!FetchingState.isSuccess(state.pagesState.clients)) {
            yield* getClientsRequest.saga({});
        }
    } catch (e) {
        console.error(e);
    }
}

function* checkForExistingClient(a: LocationChangeAction) {
    try {
        const urls = [DashboardUrl, UsersPageUrl, CampaignUrl, EditCampaignUrl, CreateClientUrl];
        const mathes = urls.map(u => {
            const match = u.match(a.payload, true);
            return match.isMatched ? match.params.clientId : undefined;
        });

        const clientId = mathes.find(c => c !== undefined);
        let state: AppState = yield select();

        if (!clientId || clientId === "direct") {
            const match = EditCampaignUrl.match(a.payload, true);
            if (match.isMatched) {
            }

            return;
        }

        if (FetchingState.isInitial(state.pagesState.clients) || FetchingState.isError(state.pagesState.clients)) {
            yield* getClientsRequest.saga({});
        }

        let clientList: ClientDataInterface[] = undefined;
        state = yield select();

        if (FetchingState.isSuccess(state.pagesState.clients)) {
            clientList = state.pagesState.clients.data.clients;
        }

        if (clientList.find(c => c.clientId === clientId)) {
            return;
        }

        yield put(replace(DashboardUrl.format({ clientId: clientList[0].clientId })));
    } catch (e) {
        console.error(e);
    }
}

function* redirectWithClientId(a: LocationChangeAction) {
    try {
        const response: typeof getClientsRequest.types.okResult = yield* getClientsRequest.saga({});
        if (response) {
            if (response.clients && response.clients.length > 0) {
                const clientId: string = response.clients[0].clientId;
                yield put(replace(DashboardUrl.format({ clientId: clientId })));
            } else {
                yield put(replace(DashboardUrl.format({ clientId: "direct" })));
            }
        }
    } catch (e) {
        console.error(e);
    }
}

function* goToReportingForReportingOnlyClients(a: LocationChangeAction) {
    const dashboardUrlMatch = DashboardUrl.match(a.payload, true);

    function* redirectToReporting() {
        const appState: AppState = yield select();
        const onlyReporting = isReportingOnlyCustomer(appState);

        if (onlyReporting) {
            if (dashboardUrlMatch.isMatched) {
                yield put(
                    replace(
                        ReportUrl.format({
                            clientId: dashboardUrlMatch.params.clientId,
                            tab: "dynamic"
                        })
                    )
                );
            }
        }
    }

    if (dashboardUrlMatch.isMatched) {
        const appState: AppState = yield select();

        if (FetchingState.isInitialOrFetching(appState.pagesState.customer)) {
            yield take((a: Action) => getCustomerRequest.actions.isOk(a));
        }
        yield* redirectToReporting();
    }
}

function* redirectFromSignedOut(a: LocationChangeAction) {
    try {
        storeTokens({ auth_token: "", refresh_token: "" });
        yield put(replace(rootPageUrl.format({})));
    } catch (e) {
        console.error(e);
    }
}

function* getBackendVersion(a: LocationChangeAction) {
    const state: AppState = yield select();
    if (
        FetchingState.isSuccess(state.pagesState.backendVersion) ||
        FetchingState.isFetching(state.pagesState.backendVersion)
    ) {
        return;
    }

    try {
        yield getBackendVersionRequest.saga({});
    } catch (e) {
        console.error(e);
    }
}

function* redirectMobileUser(a: LocationChangeAction) {
    try {
        if (
            navigator.userAgent.match(/Android/i) ||
            navigator.userAgent.match(/webOS/i) ||
            navigator.userAgent.match(/iPhone/i) ||
            navigator.userAgent.match(/iPad/i) ||
            navigator.userAgent.match(/iPod/i) ||
            navigator.userAgent.match(/BlackBerry/i) ||
            navigator.userAgent.match(/Windows Phone/i)
        ) {
            yield put(replace(MobilePageUrl.format({})));
            return;
        }

        return;
    } catch (e) {
        console.error(e);
    }
}

function* refreshDashboardLink(a: LocationChangeAction) {
    const appState: AppState = yield select();
    const clientIdRegExp = /\/\d+/;
    if (!appState.route.current || !appState.route.previous) {
        return;
    }
    const currentClientId = appState.route.current.pathname.match(clientIdRegExp);
    const prevClientId = appState.route.previous.pathname.match(clientIdRegExp);

    try {
        if (currentClientId && prevClientId && currentClientId[0] !== prevClientId[0]) {
            yield put(changeDashboardLinkAction(undefined));
        }
        return;
    } catch (e) {
        console.error(e);
    }
}

function* sendSupportMessage(action: typeof sendSupportMessageAction.typeInterface) {
    const message = action.message;
    if (message === "" || message === undefined) {
        yield put({
            type: openNotificationAction.type,
            isOpen: true,
            isWarning: true,
            message: "You can not send empty message"
        });

        return;
    }

    try {
        const response: typeof postSupportMessageRequest.types.okResult = yield* postSupportMessageRequest.saga(
            {},
            { message: message }
        );

        if (response) {
            yield put({
                type: openNotificationAction.type,
                isOpen: true,
                message: "Thank you for your message, we will be in touch shortly"
            });
        }
    } catch (e) {
        console.error(e);

        yield put({
            type: openNotificationAction.type,
            isOpen: true,
            isWarning: true,
            message: "Sorry, something has gone wrong! Please try again later"
        });
    }
}

export function* pagesSaga() {
    yield all([
        takeEvery(
            (a: LocationChangeAction) =>
                a.type === LOCATION_CHANGE &&
                !rootPageUrl.match(a.payload, true).isMatched &&
                !LogoutPageUrl.match(a.payload, true).isMatched,
            function*(a) {
                yield all([call(checkForExistingClient, a), call(loadCustomerAndClients, a)]);
            }
        ),
        takeEvery(
            (a: LocationChangeAction) =>
                a.type === LOCATION_CHANGE &&
                !rootPageUrl.match(a.payload, true).isMatched &&
                !LogoutPageUrl.match(a.payload, true).isMatched,
            refreshDashboardLink
        ),
        takeEvery(
            (a: LocationChangeAction) => a.type === LOCATION_CHANGE && AppPageUrl.match(a.payload, true).isMatched,
            redirectWithClientId
        ),
        takeEvery(
            (a: LocationChangeAction) => a.type === LOCATION_CHANGE && LogoutPageUrl.match(a.payload, true).isMatched,
            redirectFromSignedOut
        ),
        takeEvery(
            (a: LocationChangeAction) => a.type === LOCATION_CHANGE && !MobilePageUrl.match(a.payload, true).isMatched,
            redirectMobileUser
        ),
        takeEvery(
            (a: LocationChangeAction) =>
                a.type === LOCATION_CHANGE &&
                !rootPageUrl.match(a.payload, true).isMatched &&
                !LogoutPageUrl.match(a.payload, true).isMatched,
            getBackendVersion
        ),
        takeEvery((a: LocationChangeAction) => a.type === LOCATION_CHANGE, goToReportingForReportingOnlyClients),
        takeEvery(sendSupportMessageAction.type, sendSupportMessage)
    ]);
}
